import React, { useState, useEffect } from "react";
import { Settings, Download, Users, Activity, Star } from "lucide-react";

const StatsTeamSection = ({ isDarkMode = false }) => {
  const [counters, setCounters] = useState({
    downloads: 0,
    clients: 0,
    users: 0,
    rates: 0,
  });

  const targetValues = {
    downloads: 2067,
    clients: 982,
    users: 890,
    rates: 5,
  };

  const stats = [
    {
      icon: <Download className="w-8 h-8 text-black" />,
      number: counters.downloads,
      target: targetValues.downloads,
      label: "Total Downloads",
    },
    {
      icon: <Users className="w-8 h-8 text-black" />,
      number: counters.clients,
      target: targetValues.clients,
      label: "Total Experts",
    },
    {
      icon: <Activity className="w-8 h-8 text-black" />,
      number: counters.users,
      target: targetValues.users,
      label: "App Reviews",
    },
    {
      icon: <Star className="w-8 h-8 text-black" />,
      number: counters.rates,
      target: targetValues.rates,
      label: "App Rating",
    },
  ];

  // Counter animation effect
  useEffect(() => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    const stepTime = duration / steps;

    const intervals = Object.keys(targetValues).map((key) => {
      const target = targetValues[key];
      const increment = target / steps;
      let current = 0;

      return setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(intervals.find((interval) => interval === this));
        }
        setCounters((prev) => ({
          ...prev,
          [key]: Math.floor(current),
        }));
      }, stepTime);
    });

    return () => intervals.forEach(clearInterval);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={`font-['Poppins'] ${isDarkMode ? "bg-black" : "bg-white"}`} style={{ fontFamily: 'Poppins, sans-serif' }}>
      {/* Statistics Section */}
      <section
        className={`relative py-20 lg:py-20 overflow-hidden ${
          isDarkMode ? "bg-black" : "bg-white"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16 animate-fade-in-up">
            <h2
              className={`text-4xl sm:text-5xl font-semibold mb-8 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Growing Together
            </h2>
            <p
              className={`text-lg leading-relaxed max-w-3xl mx-auto ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Join thousands of construction professionals who are already
              building their future with Homeegram. Our community grows stronger every day.
            </p>
          </div>

          {/* Statistics Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center group animate-fade-in-up"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                {/* Icon */}
                <div className="flex justify-center mb-6">
                  <div
                    className={`w-16 h-16 rounded-lg flex items-center justify-center group-hover:scale-110 transition-all duration-300 ${
                      isDarkMode
                        ? "bg-gray-800 text-[#FEF200] group-hover:bg-gray-700"
                        : "bg-[#FEF200] text-white group-hover:bg-yellow-500"
                    }`}
                  >
                    {stat.icon}
                  </div>
                </div>

                {/* Number */}
                <div
                  className={`text-4xl sm:text-5xl lg:text-6xl font-semibold mb-4 group-hover:text-[#FEF200] transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {stat.number.toLocaleString()}
                </div>

                {/* Label */}
                <div
                  className={`text-lg font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* Additional Growth Metrics */}
          <div className="mt-20 grid md:grid-cols-3 gap-8 animate-fade-in-up" style={{ animationDelay: "0.8s" }}>
            <div 
              className={`text-center p-6 rounded-2xl ${
                isDarkMode ? "bg-gray-800" : "bg-white"
              } shadow-lg hover:shadow-xl transition-all duration-300`}
            >
              <h3 
                className={`text-2xl font-semibold mb-2 ${
                  isDarkMode ? "text-[#FEF200]" : "text-black"
                }`}
              >
                500+ Cities
              </h3>
              <p 
                className={`text-sm ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Serving construction professionals across India
              </p>
            </div>

            <div 
              className={`text-center p-6 rounded-2xl ${
                isDarkMode ? "bg-gray-800" : "bg-white"
              } shadow-lg hover:shadow-xl transition-all duration-300`}
            >
              <h3 
                className={`text-2xl font-semibold mb-2 ${
                  isDarkMode ? "text-[#FEF200]" : "text-black"
                }`}
              >
                95% Satisfaction
              </h3>
              <p 
                className={`text-sm ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                User satisfaction rate based on platform experience
              </p>
            </div>

            <div 
              className={`text-center p-6 rounded-2xl ${
                isDarkMode ? "bg-gray-800" : "bg-white"
              } shadow-lg hover:shadow-xl transition-all duration-300`}
            >
              <h3 
                className={`text-2xl font-semibold mb-2 ${
                  isDarkMode ? "text-[#FEF200]" : "text-black"
                }`}
              >
                24/7 Support
              </h3>
              <p 
                className={`text-sm ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Round-the-clock assistance for all users
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Scroll to Top Button */}
      <div className="fixed bottom-8 right-8 z-50">
        <button 
          onClick={scrollToTop}
          className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 ${
            isDarkMode
              ? "bg-gray-800 hover:bg-gray-700 text-white"
              : "bg-black hover:bg-gray-800 text-white"
          }`}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 15l7-7 7 7"
            />
          </svg>
        </button>
      </div>

      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
        }

        @keyframes countUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .counter-animation {
          animation: countUp 0.6s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default StatsTeamSection;